import { filterNotionPageData } from "functions/filterNotionPageData";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useMemo } from "react";
import notionDataService from "services/notiondata.service";


interface LandingPageProps {
  landingPage: LandingPageType;
  locale: string;
}
type ButtonProps = {
  text: string;
  textColor?: string;
  buttonUrl?: string;
  bgColor?: string;
};

type SubtitleProps = {
  title: string;
  textColor?: string;
};


type PublishDate = {
  start: string;
  end?: string;
};

type LandingPageType = {
  title: string[] | string;
  titleTextColor: string;
  slug: string;
  publishStatus: string;
  publishDate?: PublishDate;
  description: string;
  descriptionTextColor?: string;
  termsConditionsTextColor?: string;
  termsConditions?: string;
  region: string[];
  id: string;
  buttonProps: ButtonProps;
  buttonProps2?: ButtonProps;
  subtitleProps?: SubtitleProps;
  bgColor: string;
  metaProps: {
    title?: string;
    description?: string;
  };
  desktopImage?: string;
  mobileImage?: string;
};
const LandingPage = ({ landingPage }: LandingPageProps) => {

  const router = useRouter();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const currentPath = window.location.pathname;
      if (currentPath.startsWith('/international')) {
        const newPath = currentPath.replace('/international', '');
        router.replace(newPath);
      }
    }
  }, [router]);

  const pageTitle = useMemo(() => {
    if (landingPage.metaProps.title) {
      return landingPage.metaProps.title
    }
    if (Array.isArray(landingPage.title)) {
      return landingPage.title.join(' ')
    }
    return landingPage.title
  }
    , [landingPage.metaProps.title, landingPage.title]);

  const replaceToNewLine = (str: string) => {
    return str.replace(/\n/g, ' ');
  };

  const metaDescription = useMemo(() => {
    if (landingPage.metaProps.description) {
      return replaceToNewLine(landingPage.metaProps.description)
    }

    //get first 160 characters of description
    if (Array.isArray(landingPage.description)) {
      return replaceToNewLine(landingPage.description.join(' ').substring(0, 160))
    }
    return replaceToNewLine(landingPage.description.substring(0, 160))
  }
    , [landingPage.description, landingPage.metaProps.description]);

  const getImageAltText = () => {
    if (Array.isArray(landingPage.title) && landingPage.title.join(' ').toLowerCase().includes('black friday')) {
      switch (router.locale) {
        case 'au':
          return "Black Friday deal from Dresden Vision Australia: '50% OFF ALL GLASSES' in bold white text on a purple background, featuring a woman wearing red Dresden glasses.";
        case 'nz':
          return "Black Friday deal from Dresden Vision New Zealand: '50% OFF ALL GLASSES' in bold white text on a purple background, featuring a woman wearing red Dresden glasses.";
        case 'ca':
          return "Black Friday deal from Dresden Vision Canada: '50% OFF ALL GLASSES' in bold white text on a purple background, featuring a woman wearing red Dresden glasses.";
        default:
          return "Black Friday deal from Dresden Vision: '50% OFF ALL GLASSES' in bold white text on a purple background, featuring a woman wearing red Dresden glasses.";
      }
    }
    return `Image of ${landingPage.title}`;
  };

  return (
    <>
      <Head>
        <title>{`${pageTitle} | Dresden Vision`}</title>
        <meta name="twitter:title" content={`${pageTitle}`} />
        <meta name="og:title" content={`${pageTitle}`} />
        <meta name="description" content={`${metaDescription}`} />
        <meta property="og:description" content={`${metaDescription}`} />
        <meta name="twitter:description" content={`${metaDescription}`} />
        {landingPage.desktopImage && (<meta property="og:image" content={`${landingPage.desktopImage}`} />)}
        {landingPage.desktopImage && (<meta property="twitter:image" content={`${landingPage.desktopImage}`} />)}
        <meta name="robots" content="noindex,nofollow" />
      </Head>
      <section style={{ backgroundColor: landingPage.bgColor ?? '#fff' }}>
        <div className="mx-auto flex max-w-[1224px] px-3">
          <div className="lg:grid lg:grid-cols-11 flex flex-col" style={{ backgroundColor: landingPage.bgColor ?? '#fff' }}>
            <div className="col-span-6 lg:pr-16 py-[30px] lg:py-10 flex flex-col gap-[30px] lg:gap-2 items-center justify-center">
              <div className="flex flex-col w-full text-center lg:text-left">
                {
                  Array.isArray(landingPage.title) ? (
                    landingPage.title.map((title: string, index: number) => (
                      <p key={index} className={`font-semibold md:text-[40px] xsm:text-[25px] xsm:text-center md:text-left`} style={{ color: landingPage.titleTextColor ?? '#fff' }}>{title}</p>
                    ))
                  ) : (
                    <p className={`font-semibold md:text-[40px] xsm:text-[25px] xsm:text-center md:text-left`} style={{ color: landingPage.titleTextColor ?? '#fff' }}>{landingPage.title}</p>
                  )
                }
              </div>
              {landingPage.subtitleProps?.title && (<div className="flex flex-col w-full text-center lg:text-left">
                <p className={`font-medium md:text-[25px] xsm:text-[17px] xsm:text-center md:text-left`} style={{ color: landingPage.subtitleProps.textColor ?? '#fff' }}>{landingPage.subtitleProps.title}</p>
              </div>)}
              <div className="relative w-full h-[367px] block lg:hidden">
                <Image
                  src={landingPage.mobileImage ?? landingPage.desktopImage ?? ''}
                  alt={getImageAltText()}
                  fill={true}
                  style={{ objectFit: "cover" }}
                  priority
                />
              </div>
              <div className="w-full px-[5px] lg:px-0 mb-2">
                <p className={`font-normal text-base  xsm:text-left md:block md:text-left`}
                  style={{ color: landingPage.descriptionTextColor ?? '#fff' }} dangerouslySetInnerHTML={{ __html: landingPage.description }}></p>
              </div>
              {landingPage.buttonProps.buttonUrl && (
                <div className="w-full text-center lg:text-left  cursor-pointer">
                  <Link
                    prefetch={false}
                    href={landingPage.buttonProps.buttonUrl}
                    className="text-[20px] font-medium leading-[18px]"
                    style={{ color: landingPage.buttonProps.textColor ?? '#fff' }}>
                    <button
                      className={`font-medium md:max-w-[300px] md:w-[75%] w-full py-4 rounded-[6px]  cursor-pointer`}
                      style={{ backgroundColor: landingPage.buttonProps.bgColor ?? '#fff', color: landingPage.buttonProps.textColor ?? landingPage.bgColor ?? '#000' }}>
                      {landingPage.buttonProps.text}
                    </button>
                  </Link>
                </div>
              )}
              {landingPage.buttonProps2?.buttonUrl && (
                <div className="w-full text-center lg:text-left  cursor-pointer">
                  <Link
                    prefetch={false}
                    href={landingPage.buttonProps2.buttonUrl}
                    className="text-[20px] font-medium leading-[18px]"
                    style={{ color: landingPage.buttonProps2.textColor ?? '#fff' }}>
                    <button
                      className={`font-medium md:max-w-[300px] md:w-[75%] w-full py-4 rounded-[6px]`}
                      style={{ backgroundColor: landingPage.buttonProps2.bgColor ?? '#fff', color: landingPage.buttonProps2.textColor ?? landingPage.bgColor ?? '#000' }}>
                      {landingPage.buttonProps2.text}
                    </button>
                  </Link>
                </div>
              )}

              {landingPage.termsConditions && (<div className="w-full px-[5px] lg:px-0 text-left lg:text-left mt-3">
                <p className={`text-[12px] lg:w-[525px] shop:w-[595px] leading-[14px]`}
                  style={{ color: landingPage.termsConditionsTextColor ?? '#fff' }}>
                  <span className="font-semibold">T&C’s:</span> {landingPage.termsConditions}
                </p>
              </div>)}
            </div>
            {landingPage.desktopImage && (<div className="relative w-full col-span-5 h-full hidden lg:block">
              <Image
                src={landingPage.desktopImage}
                alt={getImageAltText()}
                fill={true}
                style={{ objectFit: "cover" }}
                priority
              />
            </div>)}
          </div>
        </div>
      </section>
    </>
  );

};


export const getServerSideProps = async ({
  params,
  locale
}: {
  params: { landingPage: string };
  locale: string;
}) => {
  // Perform any necessary data fetching or processing here
  // For example, you can fetch data from an API based on the landingPage and locale
  const pageData: LandingPageType[] = await notionDataService.getPageList('landingpage', locale, false);

  //Filter pageData with params.landingPage.slug and handle non existing case
  let selectedPages = pageData.filter((page: LandingPageType) => page.slug == params.landingPage);

  //If no page is found, redirect to home page
  if (selectedPages.length === 0) {
    return {
      redirect: {
        destination: '/' + (locale === 'international' ? '' : `/${locale}`),
        permanent: false,
      },
    }
  }

  //Filter out pages that are not published or not for the current environment
  selectedPages = selectedPages.filter((item: any) => filterNotionPageData(item, locale));

  if (selectedPages.length === 0) {
    //redirect to main page
    return {
      redirect: {
        destination: '/' + (locale == 'international' ? '' : `/${locale}`),
        permanent: false,
      },
    }

  }

  const selectedPage = selectedPages[0];

  return {
    props: {
      landingPage: selectedPage,
      locale,
    },
  };
}


export default LandingPage;
